import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';
import React from "react";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 'bold' }]
});

const fullScreenStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'white'
  }
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 15,
    paddingBottom: 25,
    paddingHorizontal: 25
  },
  boldText: {
    fontFamily: 'Roboto',
    fontWeight: 'bold'
  },
  title: {
    fontFamily: 'Roboto',
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 6
  },
  infoBox: {
    display: 'flex',
    flexDirection: 'row',
    border: '1px solid black',
    padding: 8,
    marginBottom: 8
  },
  infoCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 3
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  infoLabel: {
    fontSize: 8,
    fontFamily: 'Roboto',
    width: '38%'
  },
  infoValue: {
    fontSize: 8,
    width: '62%'
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    paddingVertical: 3,
    marginTop: 2
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    paddingVertical: 2
  },
  sectionHeading: {
    fontSize: 9,
    fontFamily: 'Roboto',
    marginTop: 4,
    marginBottom: 2
  },
  sectionTotalRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    borderTop: '1px solid black',
    paddingVertical: 2,
    marginBottom: 2
  },
  // table column widths (must add up to 100%)
  colDate: { fontSize: 8, width: '11%', textAlign: 'left' },
  colDesc: { fontSize: 8, width: '29%', textAlign: 'left' },
  colBatch: { fontSize: 8, width: '9%', textAlign: 'center' },
  colExpiry: { fontSize: 8, width: '9%', textAlign: 'center' },
  colRate: { fontSize: 8, width: '11%', textAlign: 'right' },
  colQty: { fontSize: 8, width: '6%', textAlign: 'right' },
  colDisc: { fontSize: 8, width: '11%', textAlign: 'right' },
  colAmt: { fontSize: 8, width: '14%', textAlign: 'right' },
  summaryRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2
  },
  summaryLabel: {
    fontSize: 8,
    fontFamily: 'Roboto'
  },
  summaryValue: {
    fontSize: 8,
    textAlign: 'right'
  },
  receiptHeader: {
    display: 'flex',
    flexDirection: 'row',
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    paddingVertical: 2
  }
});

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ fontSize: 8, ...style }} {...props}>
    {content}
  </PDFText>
);

// Indian-format currency: 5034.5 -> "5,034.50"
const fmt = (n) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Amount in words (Indian numbering system)
const numberToWords = (num) => {
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const twoDigits = (n) => (n < 20 ? ones[n] : `${tens[Math.floor(n / 10)]}${n % 10 ? ' ' + ones[n % 10] : ''}`);
  const threeDigits = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return `${h ? ones[h] + ' hundred' + (r ? ' and ' : '') : ''}${r ? twoDigits(r) : ''}`;
  };
  let n = Math.round(Number(num) || 0);
  if (n === 0) return 'zero';
  let str = '';
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  if (crore) str += `${threeDigits(crore)} crore `;
  if (lakh) str += `${twoDigits(lakh)} lakh `;
  if (thousand) str += `${twoDigits(thousand)} thousand `;
  if (n) str += `${str && n < 100 ? 'and ' : ''}${threeDigits(n)}`;
  return str.trim();
};

export const IndoorBillManagementGroupReportPdf = ({ data, isPublic = false }) => {
  const [barcodeImg, setBarcodeImg] = React.useState("");
  const [qrImg, setQrImg] = React.useState("");
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isDownloaded, setIsDownloaded] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const formatRegTime = (time) => {
    if (!time || time === '-') return '-';
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    const options = { hour: '2-digit', minute: '2-digit', hour12: true };
    return date.toLocaleTimeString('en-US', options);
  };

  // ── Totals: identical formulas to the other indoor billing reports ──
  const totalSubTotalAmount = data?.indoorService?.reduce((total, item) => total + parseFloat(item.total_amt || 0), 0);
  const totalSubTotalRoomAmount = data?.roomChargeTracker?.reduce(
    (total, item) => total + parseFloat(item.charge || 0),
    0
  );
  const totalRoomCharge = data?.roomChargeTracker?.reduce((total, item) => total + parseFloat(item.net_amt || 0), 0);
  const totalNetAmount = data?.indoorService?.reduce((total, item) => total + parseFloat(item.net_amt || 0), 0);
  const totalDiscountAmt = data?.indoorService?.reduce((total, item) => total + parseFloat(item.disc_amt || 0), 0);
  const addTotalDiscountAmt = data?.indoorService?.reduce(
    (total, item) => total + parseFloat(item.add_discount || 0),
    0
  );
  const addTotalDiscountRoomAmt = data?.roomChargeTracker?.reduce(
    (total, item) => total + parseFloat(item.disc_amt || 0),
    0
  );
  const amountReceivedByIndoorservice = data?.indoorService?.reduce(
    (total, item) => total + parseFloat(item.total_recpt || 0),
    0
  );
  const amountReceivedByReceiptVoucher = data?.receiptVouchers?.reduce(
    (total, item) => total + parseFloat(item.received_amount || 0),
    0
  );
  const amountRefundByRefundVoucher = data?.paymentRefundVouchers?.reduce(
    (total, item) => total + parseFloat(item?.amount || 0),
    0
  );

  const grossTotal = totalSubTotalAmount + totalSubTotalRoomAmount;
  const totalDiscount = totalDiscountAmt + addTotalDiscountAmt + addTotalDiscountRoomAmt;
  const netBillAmount = totalRoomCharge + totalNetAmount;
  const amountReceived = amountReceivedByIndoorservice + amountReceivedByReceiptVoucher;
  const balance =
    grossTotal + amountRefundByRefundVoucher - (amountReceived + totalDiscount);

  const billingDate = formatDate(data?.billing_date);

  // ── ROOMS section: one row per ward (qty = days) ──
  const groupedData = data?.roomChargeTracker.reduce((acc, item) => {
    if (!acc[item.ward_name]) {
      acc[item.ward_name] = {
        ward_name: item.ward_name,
        room_no: item.room_no,
        bed_no: item.bed_no,
        date: item.date,
        Rate: item.charge,
        Disc: item.disc_amt,
        totalQuantity: 0,
        totalNetAmount: 0,
        items: []
      };
    }
    acc[item.ward_name].totalNetAmount += item.net_amt;
    acc[item.ward_name].totalQuantity += 1;
    acc[item.ward_name].items.push(item);
    return acc;
  }, {});
  const groupedDataArray = Object.values(groupedData);
  const roomsTotal = groupedDataArray.reduce((sum, g) => sum + Number(g.totalNetAmount || 0), 0);

  // ── Service sections grouped by sub_group_name, rows date-wise ──
  const allServiceItems = data.indoorService.flatMap((indoorServiceItem) => indoorServiceItem.service_items);
  const groupedServiceItems = allServiceItems.reduce((acc, item) => {
    const group = item.sub_group_name.toLowerCase();
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});
  Object.keys(groupedServiceItems).forEach((group) => {
    groupedServiceItems[group].sort((a, b) => new Date(a.billingDate) - new Date(b.billingDate));
  });
  const sortedGroupNames = Object.keys(groupedServiceItems).sort((a, b) => {
    const firstLetterA = a.charAt(0).toLowerCase();
    const firstLetterB = b.charAt(0).toLowerCase();
    return firstLetterA.localeCompare(firstLetterB);
  });
  const sectionTotal = (groupName) =>
    groupedServiceItems[groupName].reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

  React.useEffect(() => {
    if (!data || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const refId = params.get("refId");

    // BARCODE
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(data.ipd_no), {
      format: "CODE128",
      displayValue: false,
      height: 40
    });
    setBarcodeImg(canvas.toDataURL());
    let path;
    // QR → PUBLIC LINK
    if (refId) {
      if (isPublic) {
        path = window.location.pathname;
      } else {
        path = window.location.pathname.replace("/print/", "/public/print/");
      }
      const qrUrl = `${window.location.origin}${path}?refId=${refId}`;
      QRCode.toDataURL(qrUrl).then(setQrImg);
    }
  }, [data]);

  const downloadPDF = async () => {
    try {
      // Import pdf dynamically to avoid SSR issues
      const { pdf } = await import('@react-pdf/renderer');
      const blob = await pdf(PdfDoc).toBlob();
      const url = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `Final-Bill-${data.finalBillNo || Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => {
        URL.revokeObjectURL(url);
        setIsDownloading(false);

        // Show success message instead of trying to close
        setIsDownloaded(true);
      }, 100);

    } catch (error) {
      console.error('Error downloading PDF:', error);
      setIsDownloading(false);
      setIsError(true);
    }
  };

  React.useEffect(() => {
    if (isPublic && data && barcodeImg && qrImg && !isDownloading) {
      // Small delay to ensure everything is loaded
      const timer = setTimeout(() => {
        downloadPDF();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isPublic, data, barcodeImg, qrImg, isDownloading]);

  const PdfDoc = (
    <Document title={`${data.patientName}'s Final Bill`}>
      <Page size="A4">
        <PDFImage
          src="/logo_Hospital_m.jpeg"
          style={{ width: '100%', height: '70px', marginBottom: 4 }}
        />

        <Text
          style={styles.title}
          content={`Final Bill Details   ${data.billLabel === 'Final Bill' ? 'Final' : 'Provisional'}`}
        />

        {/* ── Barcode (left) + QR (right) ── */}
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 4
          }}
        >
          <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {barcodeImg && <PDFImage src={barcodeImg} style={{ width: 140, height: 40 }} />}
            <Text style={{ fontSize: 7, marginTop: 1 }} content="( IP No )" />
          </View>
          {qrImg && <PDFImage src={qrImg} style={{ width: 55, height: 55 }} />}
        </View>

        {/* ── Patient / bill info box ── */}
        <View style={styles.infoBox}>
          <View style={{ ...styles.infoCol, marginRight: 10 }}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Patient Name" />
              <Text style={styles.infoValue} content={`: ${data.patientName}`} />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Gender/Age" />
              <Text style={styles.infoValue} content={`: ${data.gender} / ${data.age}`} />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Mobile No" />
              <Text style={styles.infoValue} content={`: ${data.mobile || '-'}`} />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Admitting Doctor" />
              <Text style={styles.infoValue} content={`: ${data.consultant.doctor} (${data.department})`} />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Ward/Bed No" />
              <Text style={styles.infoValue} content={`: ${data?.ward || '-'} / ${data?.consultantDetail?.bed_no || '-'}`} />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Address" />
              <Text style={styles.infoValue} content={`: ${data.address}`} />
            </View>
          </View>
          <View style={styles.infoCol}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="MR No" />
              <Text style={styles.infoValue} content={`: ${data.uhid}`} />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="IP No" />
              <Text style={styles.infoValue} content={`: ${data.ipd_no}`} />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Bill No / Date" />
              <Text style={styles.infoValue} content={`: ${data.finalBillNo || '-'}  /  ${billingDate}`} />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Admission Date/Time" />
              <Text
                style={styles.infoValue}
                content={`: ${formatDate(data?.admission_date)}  ${formatRegTime(data?.admission_time)}`}
              />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Discharge Date/Time" />
              <Text
                style={styles.infoValue}
                content={`: ${data?.dischargeDate === '-' ? '-' : formatDate(data?.dischargeDate)}  ${formatRegTime(data?.dischargeTime)}`}
              />
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel} content="Billing Category" />
              <Text style={styles.infoValue} content={`: ${data.sponsor || '-'}`} />
            </View>
          </View>
        </View>

        {/* ── Table header ── */}
        <View style={styles.tableHeader}>
          <Text style={{ ...styles.colDate, fontFamily: 'Roboto' }} content="Date" />
          <Text style={{ ...styles.colDesc, fontFamily: 'Roboto' }} content="Description" />
          <Text style={{ ...styles.colBatch, fontFamily: 'Roboto' }} content="Batch No" />
          <Text style={{ ...styles.colExpiry, fontFamily: 'Roboto' }} content="Expiry Dt" />
          <Text style={{ ...styles.colRate, fontFamily: 'Roboto' }} content="Rate" />
          <Text style={{ ...styles.colQty, fontFamily: 'Roboto' }} content="Qty" />
          <Text style={{ ...styles.colDisc, fontFamily: 'Roboto' }} content="Discount" />
          <Text style={{ ...styles.colAmt, fontFamily: 'Roboto' }} content="Amount" />
        </View>

        {/* ── ROOMS section ── */}
        {groupedDataArray.length > 0 && (
          <>
            <Text style={styles.sectionHeading} content="ROOMS" />
            {groupedDataArray.map((group, index) => (
              <View style={styles.row} key={`room-${index}`}>
                <Text style={styles.colDate} content={formatDate(group.date)} />
                <Text style={styles.colDesc} content={`${group.ward_name} / ${group.room_no} / ${group.bed_no}`} />
                <Text style={styles.colBatch} content="" />
                <Text style={styles.colExpiry} content="" />
                <Text style={styles.colRate} content={fmt(group.Rate)} />
                <Text style={styles.colQty} content={String(group.totalQuantity)} />
                <Text style={styles.colDisc} content={fmt(group.Disc)} />
                <Text style={styles.colAmt} content={fmt(group.totalNetAmount)} />
              </View>
            ))}
            <View style={styles.sectionTotalRow}>
              <Text style={{ fontSize: 8, fontFamily: 'Roboto' }} content={`ROOMS TOTAL :   ${fmt(roomsTotal)}`} />
            </View>
          </>
        )}

        {/* ── Service sections by sub group ── */}
        {sortedGroupNames.map((groupName, groupIndex) => (
          <React.Fragment key={`grp-${groupIndex}`}>
            <Text style={styles.sectionHeading} content={groupName.trim().toUpperCase()} />
            {groupedServiceItems[groupName].map((serviceItem, serviceIndex) => (
              <View style={styles.row} key={`${groupIndex}-${serviceIndex}`}>
                <Text style={styles.colDate} content={formatDate(serviceItem.billingDate)} />
                <Text style={styles.colDesc} content={serviceItem.service_name} />
                <Text style={styles.colBatch} content="" />
                <Text style={styles.colExpiry} content="" />
                <Text style={styles.colRate} content={fmt(serviceItem.rate)} />
                <Text style={styles.colQty} content={String(serviceItem.quantity)} />
                <Text style={styles.colDisc} content={fmt(serviceItem.disc_amt)} />
                <Text style={styles.colAmt} content={fmt(serviceItem.amount)} />
              </View>
            ))}
            <View style={styles.sectionTotalRow}>
              <Text
                style={{ fontSize: 8, fontFamily: 'Roboto' }}
                content={`${groupName.trim().toUpperCase()} TOTAL :   ${fmt(sectionTotal(groupName))}`}
              />
            </View>
          </React.Fragment>
        ))}

        {/* ── Amount in words ── */}
        <Text
          style={{ fontSize: 8, fontFamily: 'Roboto', marginTop: 4 }}
          content={`Amount In Words : Rupees ${numberToWords(netBillAmount)} only`}
        />

        {/* ── Receipt details (left) + summary (right) ── */}
        <View style={{ display: 'flex', flexDirection: 'row', marginTop: 6 }}>
          <View style={{ width: '58%', paddingRight: 10 }}>
            <Text style={{ fontSize: 9, fontFamily: 'Roboto', marginBottom: 2 }} content="Receipt Details" />
            <View style={styles.receiptHeader}>
              <Text style={{ fontSize: 8, fontFamily: 'Roboto', width: '8%' }} content="SNo" />
              <Text style={{ fontSize: 8, fontFamily: 'Roboto', width: '22%' }} content="Record date" />
              <Text style={{ fontSize: 8, fontFamily: 'Roboto', width: '22%' }} content="Receipt No" />
              <Text style={{ fontSize: 8, fontFamily: 'Roboto', width: '26%' }} content="Payment Mode" />
              <Text style={{ fontSize: 8, fontFamily: 'Roboto', width: '22%', textAlign: 'right' }} content="Amount" />
            </View>
            {data.receiptVouchers && data.receiptVouchers.length > 0 ? (
              <>
                {data.receiptVouchers.map((voucher, index) => (
                  <View style={styles.row} key={voucher._id}>
                    <Text style={{ fontSize: 8, width: '8%' }} content={String(index + 1)} />
                    <Text style={{ fontSize: 8, width: '22%' }} content={formatDate(voucher?.date)} />
                    <Text style={{ fontSize: 8, width: '22%' }} content={voucher.receipt_no || '-'} />
                    <Text style={{ fontSize: 8, width: '26%' }} content={voucher.payment_mode || '-'} />
                    <Text
                      style={{ fontSize: 8, width: '22%', textAlign: 'right' }}
                      content={fmt(voucher.received_amount)}
                    />
                  </View>
                ))}
                <View style={{ ...styles.sectionTotalRow }}>
                  <Text
                    style={{ fontSize: 8, fontFamily: 'Roboto' }}
                    content={`Grand Total :   ${fmt(amountReceivedByReceiptVoucher)}`}
                  />
                </View>
              </>
            ) : (
              <Text style={{ fontSize: 8, marginTop: 2 }} content="No receipts found" />
            )}

            {data.paymentRefundVouchers && data.paymentRefundVouchers.length > 0 && (
              <>
                <Text
                  style={{ fontSize: 9, fontFamily: 'Roboto', marginTop: 6, marginBottom: 2 }}
                  content="Refund Details"
                />
                <View style={styles.receiptHeader}>
                  <Text style={{ fontSize: 8, fontFamily: 'Roboto', width: '8%' }} content="SNo" />
                  <Text style={{ fontSize: 8, fontFamily: 'Roboto', width: '22%' }} content="Record date" />
                  <Text style={{ fontSize: 8, fontFamily: 'Roboto', width: '22%' }} content="Doc No" />
                  <Text style={{ fontSize: 8, fontFamily: 'Roboto', width: '26%' }} content="Payment Mode" />
                  <Text
                    style={{ fontSize: 8, fontFamily: 'Roboto', width: '22%', textAlign: 'right' }}
                    content="Amount"
                  />
                </View>
                {data.paymentRefundVouchers.map((voucher, index) => (
                  <View style={styles.row} key={voucher._id}>
                    <Text style={{ fontSize: 8, width: '8%' }} content={String(index + 1)} />
                    <Text style={{ fontSize: 8, width: '22%' }} content={formatDate(voucher?.date)} />
                    <Text style={{ fontSize: 8, width: '22%' }} content={voucher.doc_no || '-'} />
                    <Text style={{ fontSize: 8, width: '26%' }} content={voucher.payment_mode || '-'} />
                    <Text style={{ fontSize: 8, width: '22%', textAlign: 'right' }} content={fmt(voucher.amount)} />
                  </View>
                ))}
                <View style={{ ...styles.sectionTotalRow }}>
                  <Text
                    style={{ fontSize: 8, fontFamily: 'Roboto' }}
                    content={`Grand Total :   ${fmt(amountRefundByRefundVoucher)}`}
                  />
                </View>
              </>
            )}
          </View>

          <View style={{ width: '42%', paddingLeft: 10 }}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel} content="Gross Total" />
              <Text style={styles.summaryValue} content={fmt(grossTotal)} />
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel} content="Discount" />
              <Text style={styles.summaryValue} content={fmt(totalDiscount)} />
            </View>
            <View style={{ ...styles.summaryRow, borderTop: '1px solid black' }}>
              <Text style={styles.summaryLabel} content="Net Bill Amount" />
              <Text style={{ ...styles.summaryValue, fontFamily: 'Roboto' }} content={fmt(netBillAmount)} />
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel} content="Patient Deposit" />
              <Text style={styles.summaryValue} content={fmt(amountReceived)} />
            </View>
            {amountRefundByRefundVoucher > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel} content="Refund" />
                <Text style={styles.summaryValue} content={fmt(amountRefundByRefundVoucher)} />
              </View>
            )}
            <View style={{ ...styles.summaryRow, borderTop: '1px solid black', borderBottom: '1px solid black' }}>
              <Text style={styles.summaryLabel} content="Net Payable" />
              <Text style={{ ...styles.summaryValue, fontFamily: 'Roboto' }} content={fmt(balance)} />
            </View>
          </View>
        </View>

        {/* ── Bottom: signature ── */}
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            marginTop: 30
          }}
        >
          <Text style={{ fontSize: 8, fontFamily: 'Roboto' }} content="Authorized Signatory" />
        </View>
      </Page>
    </Document>
  )
  if (isPublic) {
    return (
      <div style={{
        backgroundColor: "white",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Arial, sans-serif",
        padding: "20px",
        textAlign: "center"
      }}>
        <div style={{ marginBottom: "20px" }}>
          <img
            src="/logo_Hospital_m.jpeg"
            alt="Hospital Logo"
            style={{ width: "200px", height: "80px", objectFit: "contain" }}
          />
        </div>

        {isDownloaded ? (
          <div style={{
            padding: "30px",
            backgroundColor: "#f0f9f0",
            borderRadius: "8px",
            maxWidth: "400px",
            border: "1px solid #4CAF50"
          }}>
            <div style={{ fontSize: "24px", color: "#4CAF50", marginBottom: "15px" }}>
              ✓
            </div>
            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#4CAF50", marginBottom: "10px" }}>
              PDF Downloaded Successfully!
            </div>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Your bill has been downloaded. You can now close this window.
            </p>
            <button
              onClick={() => window.location.href = "about:blank"}
              style={{
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "10px"
              }}
            >
              Close This Page
            </button>
          </div>
        ) : isError ? (
          <div style={{
            padding: "30px",
            backgroundColor: "#fff5f5",
            borderRadius: "8px",
            maxWidth: "400px",
            border: "1px solid #f44336"
          }}>
            <div style={{ fontSize: "24px", color: "#f44336", marginBottom: "15px" }}>
              ✗
            </div>
            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#f44336", marginBottom: "10px" }}>
              Download Failed
            </div>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Failed to download PDF. Please try again.
            </p>
            <button
              onClick={downloadPDF}
              style={{
                backgroundColor: "#1976D2",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "10px"
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div style={{
            padding: "20px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            maxWidth: "400px"
          }}>
            <div style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "15px" }}>
              {isDownloading ? "Downloading PDF..." : "Preparing PDF for download..."}
            </div>

            {isDownloading && (
              <div style={{ margin: "20px 0" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #1976D2",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto"
                }} />
                <style>
                  {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
                </style>
              </div>
            )}

            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Your bill will download automatically.
            </p>

            {!isDownloading && (
              <button
                onClick={downloadPDF}
                style={{
                  backgroundColor: "#1976D2",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginTop: "10px"
                }}
              >
                Click to Download
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={fullScreenStyles.container}>
      <PDFViewer width="100%" height="100%" showToolbar>
        {PdfDoc}
      </PDFViewer>
    </div>
  );
};
